import { HistoryOutlined } from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import {
    Button,
    Col,
    Form,
    type FormInstance,
    Input,
    InputNumber as InputNumberAntd,
    Row,
    Select,
    Switch,
} from 'antd';
import type { ProdutoCategoria } from '@/features/produto-categoria/types/ProdutoCategoria';
import type { FormProdutoValues } from '@/features/produto/types/FormProdutoValues';
import type { Produto } from '@/features/produto/types/Produto';
import type { FormAction } from '@/types/FormAction';
import { Container } from '@/components/Container';
import { InputNumber } from '@/components/input/InputNumber';
import { CustomSwitch } from '@/components/revisar/CustomSwitch';
import { useEmpresaQuery } from '@/features/empresa/hooks/useEmpresaQuery';
import { SelectMarcador } from '@/features/marcador/components/SelectMarcador';
import { MarcadorLocal } from '@/features/marcador/types/enum';
import { ProdutoCategoriaSelect } from '@/features/produto-categoria/components/ProdutoCategoriaSelect';
import { ProdutoMarcaSelect } from '@/features/produto-marca/components/ProdutoMarcaSelect';
import { useProdutoUnidadeGetQuery } from '@/features/produto/api/apiProdutoUnidadeGet';
import { ProdutoUnidadeSelect } from '@/features/produto/components/ProdutoUnidadeSelect';
import { ProdutoTipo } from '@/features/produto/types/enum';
import { roundAbnt } from '@/std/math';
import { ProdutoTabKey, ProdutoTabKeyHistoricoAlteracao } from '../useProdutoTabItems';

export function ProdutoTabGeral({
    form,
    action,
}: {
    form: FormInstance<FormProdutoValues>;
    action: FormAction;
}) {
    const navigate = useNavigate({ from: '/a/produto/$action/{-$idpk}' });

    const categoria: ProdutoCategoria | null = Form.useWatch('produto_categoria', form);

    const grade = Form.useWatch('variacao', form)?.grade;
    const temGrade = Array.isArray(grade) && grade.length > 0;

    const pro_produto_pai_idpk = Form.useWatch('pro_produto_pai_idpk', form);
    const isVariacao = typeof pro_produto_pai_idpk === 'number';

    const pro_tipo = Form.useWatch('pro_tipo', form);
    const isKit = pro_tipo === ProdutoTipo.Kit;

    const usaBalanca = Form.useWatch('pro_usa_balanca', form) === 1;
    const imprimirValidade = Form.useWatch('pro_balanca_validade', form) === 1;

    const { empresa } = useEmpresaQuery();
    const catalogoAtivo = empresa?.empresa_slug?.ems_catalogo_ativo === 'S';

    const { data } = useProdutoUnidadeGetQuery();

    const selectTipoOptions = [
        {
            label: 'Simples',
            value: 'S',
        },
        {
            label: 'Kit',
            value: 'K',
        },
        {
            label: 'Fabricado',
            value: 'F',
        },
        {
            label: 'Matéria prima',
            value: 'M',
        },
        {
            label: 'Com adicionais',
            value: 'A',
        },
    ];

    if (!isVariacao) {
        selectTipoOptions.push({
            label: 'Com variações',
            value: 'C',
        });
    }

    const onChangePrecoCusto = (): void => {
        const custo: number = form.getFieldValue('pro_preco_custo');
        const markup: number = form.getFieldValue('pro_markup_venda');
        const markupMinimo: number = form.getFieldValue('pro_markup_minimo');

        const calculoVenda = roundAbnt(custo + custo * (markup / 100));
        const calculoMinimo = roundAbnt(custo + custo * (markupMinimo / 100));

        if (markup > 0) {
            form.setFieldsValue({ pro_preco_venda: calculoVenda });
        }

        if (markupMinimo > 0) {
            form.setFieldsValue({ pro_preco_minimo: calculoMinimo });
        }
    };

    const onChangePrecoVenda = (): void => {
        const markup: number = form.getFieldValue('pro_markup_venda');
        if (markup === 0) {
            return;
        }

        const custo: number = form.getFieldValue('pro_preco_custo');
        const venda: number = form.getFieldValue('pro_preco_venda');
        const calculo = roundAbnt(((venda - custo) / custo) * 100);
        form.setFieldsValue({ pro_markup_venda: calculo });
    };

    const onChangeMarkup = (value: number): void => {
        if (!value) {
            form.setFieldsValue({ pro_preco_venda: 0 });
        }

        const custo: number = form.getFieldValue('pro_preco_custo');
        const markup: number = form.getFieldValue('pro_markup_venda');
        const calculo = roundAbnt(custo + custo * (markup / 100));
        form.setFieldsValue({ pro_preco_venda: calculo });
    };

    const onChangePrecoVendaMinimo = (): void => {
        const custo: number | undefined | null = form.getFieldValue('pro_preco_custo');
        const vendaMinimo: number = form.getFieldValue('pro_preco_minimo');
        const calculo = roundAbnt(((vendaMinimo - (custo || 0)) / (custo || 1)) * 100);
        form.setFieldsValue({ pro_markup_minimo: calculo });
    };

    const onChangeMarkupMinimo = (value: number): void => {
        if (!value) {
            form.setFieldsValue({ pro_preco_minimo: 0 });
            return;
        }

        const custo: number = form.getFieldValue('pro_preco_custo');

        if (!custo) {
            return;
        }

        const markupMinimo: number = form.getFieldValue('pro_markup_minimo');
        const calculo = roundAbnt(custo + custo * (markupMinimo / 100));

        form.setFieldsValue({ pro_preco_minimo: calculo });
    };

    const onChangeTipo = (tipo: ProdutoTipo): void => {
        form.setFieldValue('pro_tipo', tipo);

        const unidadeKit = data?.registros?.find((u) => u.pru_sigla === 'KIT');

        if (tipo === ProdutoTipo.Kit) {
            form.setFieldsValue({
                pro_controlar_estoque: 0,
                pro_markup_minimo: 0,
                pro_permite_estoque_negativo: 0,
                pro_permite_venda_fracionada: 0,
                pro_preco_minimo: 0,
                pro_produto_unidade_idpk: unidadeKit?.pru_idpk,
            });
        }
    };

    return (
        <Container>
            <Form.Item hidden={true} name='pro_produto_pai_idpk'>
                <Input type='hidden' />
            </Form.Item>

            <Row gutter={[8, 8]}>
                <Col md={8} sm={12} xs={24}>
                    <Form.Item
                        className='mb-3'
                        label='Nome do produto'
                        name='pro_descricao'
                        rules={[
                            { message: 'Por favor, forneça o nome do produto', required: true },
                        ]}
                    >
                        <Input disabled={isVariacao} />
                    </Form.Item>
                </Col>

                <Col md={8} sm={12} xs={24}>
                    <Form.Item
                        label='Código Interno'
                        name='pro_codigo_interno'
                        tooltip='Código para auxiliar na busca de produtos e localização. Deixando vazio o sistema atribui um número sequencial automaticamente.'
                    >
                        <InputNumberAntd className='w-full' />
                    </Form.Item>
                </Col>

                <Col md={8} sm={12} xs={24}>
                    <Form.Item name='pro_idpk' noStyle={true}>
                        <Input type='hidden' />
                    </Form.Item>

                    <Form.Item
                        label='Código SKU'
                        name='pro_sku'
                        tooltip='Trata-se de um código identificador único de um produto. É utilizado para controle do estoque.'
                    >
                        <Input placeholder='Código SKU' />
                    </Form.Item>
                </Col>

                <Col md={8} sm={12} xs={24}>
                    <Form.Item className='mb-3' label='Código de Barras' name='pro_cod_barra'>
                        <Input placeholder='Código de Barras (GTIN/EAN)' />
                    </Form.Item>
                </Col>

                {isKit ? null : (
                    <Col md={8} sm={12} xs={24}>
                        <Form.Item
                            className='mb-3'
                            help='Obrigatório para emissão fiscal'
                            label='Unidade'
                            name='pro_produto_unidade_idpk'
                        >
                            <ProdutoUnidadeSelect />
                        </Form.Item>
                    </Col>
                )}

                <Col md={8} sm={12} xs={24}>
                    <Form.Item className='mb-3' label='Tipo' name='pro_tipo'>
                        <Select
                            disabled={temGrade}
                            onChange={onChangeTipo}
                            options={selectTipoOptions}
                        />
                    </Form.Item>
                </Col>

                <Col md={8} sm={12} xs={24}>
                    <Form.Item
                        className='mb-3'
                        label='Categoria'
                        name='pro_produto_categoria_idpk'
                        tooltip='Para cadastrar categorias acesse: Configurações > Cadastros > Produtos > Categorias dos produtos'
                    >
                        <ProdutoCategoriaSelect
                            initialOptions={
                                categoria
                                    ? [
                                          {
                                              label: categoria.prc_caminho ?? '',
                                              value: categoria.prc_idpk,
                                          },
                                      ]
                                    : undefined
                            }
                        />
                    </Form.Item>
                </Col>

                <Col md={8} sm={12} xs={24}>
                    <Form.Item
                        noStyle={true}
                        shouldUpdate={(previous, current) =>
                            previous.produto_marca !== current.produto_marca ||
                            previous.pro_produto_marca_idpk !== current.pro_produto_marca_idpk
                        }
                    >
                        {({ getFieldValue }) => {
                            const marca: Produto['produto_marca'] = getFieldValue('produto_marca');
                            const marcaIdpk = getFieldValue('pro_produto_marca_idpk');

                            return (
                                <Form.Item
                                    className='mb-3'
                                    label='Marca'
                                    name='pro_produto_marca_idpk'
                                >
                                    <ProdutoMarcaSelect
                                        initialLabel={
                                            marca?.prm_idpk === marcaIdpk
                                                ? marca?.prm_descricao
                                                : undefined
                                        }
                                    />
                                </Form.Item>
                            );
                        }}
                    </Form.Item>
                </Col>

                <Col md={4} sm={6} xs={12}>
                    <Form.Item
                        className='mb-3'
                        label='Preço de Custo'
                        name='pro_preco_custo'
                        tooltip={
                            isKit
                                ? 'É a soma dos custos dos itens que compõem o KIT da aba "Composição do KIT"'
                                : undefined
                        }
                    >
                        <InputNumber
                            addonAfter={
                                action === 'editar' ? (
                                    <Button
                                        icon={<HistoryOutlined />}
                                        onClick={() =>
                                            void navigate({
                                                search: {
                                                    tab1: ProdutoTabKey.HistoricoAlteracoes,
                                                    tab2: ProdutoTabKeyHistoricoAlteracao.PrecoCusto,
                                                },
                                            })
                                        }
                                        title='Histórico de preços'
                                    />
                                ) : undefined
                            }
                            decimalScale={4}
                            disabled={isKit}
                            onChange={onChangePrecoCusto}
                            type='$'
                        />
                    </Form.Item>
                </Col>

                {isKit ? null : (
                    <Col md={4} sm={6} xs={12}>
                        <Form.Item className='mb-3' label='Custo Médio' name='pro_custo_medio'>
                            <InputNumber
                                addonAfter={
                                    action === 'editar' ? (
                                        <Button
                                            icon={<HistoryOutlined />}
                                            onClick={() => {
                                                void navigate({
                                                    search: {
                                                        tab1: ProdutoTabKey.HistoricoAlteracoes,
                                                        tab2: ProdutoTabKeyHistoricoAlteracao.CustoMedio,
                                                    },
                                                });
                                            }}
                                            title='Histórico de preços'
                                        />
                                    ) : undefined
                                }
                                decimalScale={4}
                                disabled={true}
                                type='$'
                            />
                        </Form.Item>
                    </Col>
                )}

                {isKit ? null : (
                    <Col md={4} sm={6} xs={12}>
                        <Form.Item
                            className='mb-3'
                            label='Markup de Venda'
                            name='pro_markup_venda'
                            tooltip='Percentual de diferença entre o preço de custo e o preço de venda. Caso não utilizar deixar 0,00.'
                        >
                            <InputNumber
                                decimalScale={2}
                                onChange={(e) => onChangeMarkup(e || 0)}
                                type='%'
                            />
                        </Form.Item>
                    </Col>
                )}

                <Col md={4} sm={6} xs={12}>
                    <Form.Item
                        className='mb-3'
                        label='Preço de Venda'
                        name='pro_preco_venda'
                        tooltip={
                            isKit
                                ? 'Valor formado na aba "Composição do KIT", composto pela soma do total representado de cada item que o compõem.'
                                : undefined
                        }
                    >
                        <InputNumber
                            addonAfter={
                                action === 'editar' ? (
                                    <Button
                                        icon={<HistoryOutlined />}
                                        onClick={() => {
                                            void navigate({
                                                search: {
                                                    tab1: ProdutoTabKey.HistoricoAlteracoes,
                                                    tab2: ProdutoTabKeyHistoricoAlteracao.PrecoVenda,
                                                },
                                            });
                                        }}
                                        title='Histórico de preços'
                                    />
                                ) : undefined
                            }
                            decimalScale={4}
                            disabled={isKit}
                            onChange={onChangePrecoVenda}
                            type='$'
                        />
                    </Form.Item>
                </Col>

                {isKit ? null : (
                    <Col md={4} sm={6} xs={12}>
                        <Form.Item
                            className='mb-3'
                            label='Markup Mínimo'
                            name='pro_markup_minimo'
                            tooltip='Percentual mínimo de diferença entre o preço de custo e o preço de venda. Caso não utilizar deixar 0,00.'
                        >
                            <InputNumber
                                decimalScale={2}
                                onChange={(e) => onChangeMarkupMinimo(e || 0)}
                                type='%'
                            />
                        </Form.Item>
                    </Col>
                )}

                {isKit ? null : (
                    <Col md={4} sm={6} xs={12}>
                        <Form.Item
                            className='mb-3'
                            label='Preço Mínimo de Venda'
                            name='pro_preco_minimo'
                            tooltip='Caso não for utilizado preço mínimo deixar 0,00'
                        >
                            <InputNumber
                                decimalScale={4}
                                onChange={onChangePrecoVendaMinimo}
                                type='$'
                            />
                        </Form.Item>
                    </Col>
                )}

                <Col lg={12} md={24} sm={24} xl={8} xs={24} xxl={8}>
                    <Form.Item
                        label='Marcadores'
                        name='pro_marcadores'
                        tooltip='Para cadastrar categorias acesse: Configurações > Cadastros > Marcadores'
                    >
                        <SelectMarcador local={MarcadorLocal.Produtos} />
                    </Form.Item>
                </Col>

                <Col lg={4} md={12} sm={12} xs={12}>
                    <Form.Item
                        getValueFromEvent={(e) => (e ? 1 : 2)}
                        getValueProps={(e) => ({ value: e === 1 })}
                        label='Status'
                        name='pro_inativo'
                        valuePropName='checked'
                    >
                        <Switch checkedChildren='Ativo' unCheckedChildren='Inativo' />
                    </Form.Item>
                </Col>

                <Col lg={4} md={12} sm={12} xs={12}>
                    <Form.Item label='Favorito' name='pro_favorito'>
                        <CustomSwitch checked='Sim' unchecked='Não' />
                    </Form.Item>
                </Col>

                <Col lg={4} md={12} sm={12} xs={12}>
                    <Form.Item label='Permite mudar preço na venda' name='pro_permite_mudar_preco'>
                        <CustomSwitch checked='Sim' unchecked='Não' />
                    </Form.Item>
                </Col>

                <Col lg={4} md={12} sm={12} xs={12}>
                    <Form.Item
                        label='Usa balança'
                        name='pro_usa_balanca'
                        tooltip={
                            <>
                                Campo utilizado para geração de um arquivo de texto para ser
                                importado nas balanças de retaguarda a partir do sistema{' '}
                                <b>Mercúrio Local</b>. Ou para leitura automática do peso na balança
                                de checkout.
                            </>
                        }
                    >
                        <CustomSwitch checked='Sim' unchecked='Não' />
                    </Form.Item>
                </Col>

                {usaBalanca && (
                    <Col lg={4} md={12} sm={12} xs={12}>
                        <Form.Item label='Imprimir Validade (Balança)' name='pro_balanca_validade'>
                            <CustomSwitch checked='Sim' unchecked='Não' />
                        </Form.Item>
                    </Col>
                )}

                {imprimirValidade && (
                    <Col md={8} sm={12} xs={12}>
                        <Form.Item
                            className='mb-3'
                            label='Dias de Validade (Balança)'
                            name='pro_balanca_validade_dias'
                        >
                            <InputNumber />
                        </Form.Item>
                    </Col>
                )}

                {catalogoAtivo && (
                    <Col lg={4} md={12} sm={12} xs={12}>
                        <Form.Item label='Exibir no catálogo' name='pro_exibir_catalogo'>
                            <CustomSwitch checked='Sim' unchecked='Não' />
                        </Form.Item>
                    </Col>
                )}
            </Row>
        </Container>
    );
}
